import React, { useEffect, useState } from "react";
import { checkWidgetAdminOrNot, URL } from "../../../utils/Constants";
import ModalWindow from "../../UI/ModalWindow";
import GranularPermissionModal from "../../Permissions/GranularPermissionModal";
import DataTable from "react-data-table-component";
import { MiniBar } from "./ListAllTeachers";
import { usePagination } from "../../../hooks/usePagination";
import Pagination from "../../UI/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Filter } from "../../../assets/icons/icon";
import FilterModal from "./FilterModal";
import { Tooltip } from "@mui/material";
import { axiosPost } from "../../../utils/AxiosApi";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";
import "./ListAllTeacher.scss";
import LineChart from "../../Charts/LineChart";

const tableStyles = {
  headCells: {
    style: {
      fontWeight: 600,
      fontSize: "13px",
      textTransform: "uppercase",
      color: "#000000de",
      background: "#fff",
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  rows: {
    style: {
      fontSize: "0.875rem",
      "&:hover": { background: "#f8fafc" },
    },
  },
  cells: {
    style: { paddingTop: 8, paddingBottom: 8 },
  },
};

const plugins = {
  legend: {
    display: false,
  },
  title: {
    display: false,
  },
  tooltip: {
    enabled: true,
  },
};

const scales = {
  x: {
    display: false,
    border: {
      display: false,
    },
  },
  y: {
    display: false,
    min: -4,
    max: 104,
    border: {
      display: false,
    },
  },
};

const buildColumns = () => {
  const all = [
    // ── Identity ──────────────────────────────────────────────────────
    {
      id: "studentName",
      name: "Student Name",
      selector: (r) => r.studentName?.trim(),
      sortable: true,
      minWidth: "150px",
      // omit: isSubject || isClass || isSection,
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 12 }}>
            {r?.studentName?.trim()}
          </div>
        </div>
      ),
    },
    {
      id: "classroomName",
      name: "Section",
      selector: (r) => r.classroomName,
      // sortable: true,
      cell: (r) => (
        <span
          style={{
            color: "#94a3b8",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
          }}
        >
          {r.yearLevelName && `Class ${r.yearLevelName} - `}
          {r.classroomName && r.classroomName}
        </span>
      ),
    },
    {
      id: "gender",
      name: "Gender",
      sortable: false,
      // omit: isDetail,
      cell: (r) => <span style={{ fontSize: 12 }}>{r.gender}</span>,
    },
    {
      id: "studentTotalPercentage",
      name: "Percent",
      selector: (r) => r.studentTotalPercentage,
      sortable: true,
      cell: (r) => <MiniBar value={r.studentTotalPercentage} />,
    },
    {
      id: "studentTotalGpa",
      name: "GPA",
      selector: (r) => r.studentTotalGpa,
      sortable: true,
      cell: (r) => <span style={{ fontSize: 12 }}>{r.studentTotalGpa}</span>,
    },
    {
      id: "grade",
      name: "Grade",
      selector: (r) => r.grade,
      sortable: true,
      cell: (r) => <span style={{ fontSize: 12 }}>{r.grade}</span>,
    },
    {
      id: "gpaEvaluation",
      name: "GPA Evaluation",
      selector: (r) => r.gpaEvaluation,
      sortable: false,
      cell: (r) => <span style={{ fontSize: 12 }}>{r.gpaEvaluation}</span>,
    },
    {
      id: "subjectsPassed",
      name: "Subject Passed",
      selector: (r) => r.subjectsPassed,
      sortable: true,
      cell: (r) => (
        <MiniBar
          value={r.subjectsPassed}
          showPercent={false}
          floatValue={false}
          total={r.totalSubjects}
        />
      ),
    },
    {
      id: "subjectsFailed",
      name: "Subject Failed",
      selector: (r) => r.subjectsFailed,
      sortable: true,
      cell: (r) => (
        <MiniBar
          value={r.subjectsFailed}
          showPercent={false}
          floatValue={false}
          total={r.totalSubjects}
        />
      ),
    },
    {
      id: "studentAssessmentPerformances",
      name: "Performance",
      selector: (r) => r.studentAssessmentPerformances,
      sortable: false,
      width: "140px",
      cell: (r) => {
        const sortedData = [...(r?.studentAssessmentPerformances ?? [])].sort(
          (a, b) =>
            new Date(a.assessmentDate).getTime() -
            new Date(b.assessmentDate).getTime(),
        );

        if (!sortedData.length) return "-";

        const labels = sortedData.map((item) => item.assessmentName);
        const values = sortedData.map((item) => item.finalPercentage);

        const chartValues =
          values.length === 1 ? [values[0], values[0]] : values;

        const isIncreasing =
          values.length < 2 || Math.max(...values) <= values.at(-1);

        const data = {
          labels,
          datasets: [
            {
              data: chartValues,
              borderColor: isIncreasing ? "#3b6d11" : "#a32d2d",
              backgroundColor: "#ffffff",
            },
          ],
        };

        return (
          <div
            style={{
              width: "100%",
              height: "50px",
            }}
          >
            <LineChart data={data} plugins={plugins} scales={scales} />
          </div>
        );
      },
    },
  ];

  return all.filter((c) => !c.omit);
};

const ListAllStudents = () => {
  const [etlLoading, setEtlLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(10);
  const [loading, setLoading] = useState(false);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    onPageChange,
    onNextPage,
    onPrevPage,
  } = usePagination(totalItems);
  const [permissionModal, setPermissionModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [filterModal, setFilterModal] = useState(false);
  const [filterData, setFilterData] = useState({
    assessmentId: "",
    educationLevelId: "",
    assessmentModuleCode: "",
    yearLevelId: "",
    classroomId: "",
    admissionId: "",
  });

  const columns = buildColumns();

  const fetchStudentsData = (page) => {
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.getStudentPerformance,
        {
          assessmentId: filterData.assessmentId,
          educationLevelId: filterData.educationLevelId,
          yearLevelId: filterData.yearLevelId,
          classroomId: filterData.classroomId,
          admissionId: filterData.admissionId,
          limit: pageSize,
          offset: pageSize * (page - 1),
        },
        (response) => {
          if (response?.status === 200) {
            const data = response?.data?.data;
            const totalRecordsCount = response?.data?.totalRecordsCount;
            setStudents(data);
            setTotalItems(totalRecordsCount);
            resolve(response);
          } else {
            reject(response);
          }
        },
        (error) => reject(error),
      );
    });
  };

  const handleStudentsData = async (page) => {
    setLoading(true);
    try {
      await fetchStudentsData(page);
    } catch (error) {
      console.log("Failed to get student performance", error);
    } finally {
      setLoading(false);
    }
  };

  const eltRefreshApi = () => {
    return new Promise((resolve, reject) => {
      axiosPost(
        URL.refreshStudentEtlBatch,
        {},
        (response) => {
          if (response?.status === 200) {
            resolve(response);
          } else {
            reject(response);
          }
        },
        (error) => {
          swal(
            "Oops",
            error?.response?.data?.message ??
              "Student performance data refreshing failed",
          );
          reject(error);
        },
      );
    });
  };

  const handleEtlRefreshBtnClick = async () => {
    setEtlLoading(true);
    try {
      await eltRefreshApi();
      setLoading(true);
      await fetchStudentsData(1);
      setCurrentPage(1);
    } catch (error) {
      console.error("Students performance refresh failed: ", error);
    } finally {
      setEtlLoading(false);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    onNextPage(handleStudentsData);
  };

  const handlePrevPage = () => {
    onPrevPage(handleStudentsData);
  };

  const handlePageChange = (page) => {
    onPageChange(page);
    handleStudentsData(page);
  };

  const handleFilter = () => {
    handleStudentsData(currentPage);
    setFilterModal(false);
  };

  useEffect(() => {
    handleStudentsData(currentPage);
  }, []);

  return (
    <>
      <div className="arr-content">
        {/* ── Header ── */}
        <div className="arr-heading" style={{ marginBottom: "24px" }}>
          <div
            className="flex header-btns"
            style={{
              gap: 8,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap-reverse",
              width: "100%",
            }}
          >
            <h1>List All Students</h1>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Tooltip title={"Filter"}>
                <button
                  aria-label="filter-btn"
                  style={{ padding: "8px 8px" }}
                  className="ghost-btn"
                  onClick={() => setFilterModal((prev) => !prev)}
                >
                  <Filter width={20} height={20} />
                </button>
              </Tooltip>

              {checkWidgetAdminOrNot("Learning Achievement") && (
                <>
                  <Tooltip title={"Refresh"}>
                    <button
                      style={{ padding: "8px 8px" }}
                      className="ghost-btn"
                      onClick={handleEtlRefreshBtnClick}
                    >
                      <FontAwesomeIcon
                        className={etlLoading ? "refresh-icon" : ""}
                        icon={faArrowsRotate}
                        fontSize={16}
                      />
                    </button>
                  </Tooltip>

                  <button
                    className="grayOutline-btn"
                    onClick={() => setPermissionModal((prev) => !prev)}
                  >
                    Permission
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div
          className="arr-table-container"
          style={{
            width: "1200px",
            minWidth: "100%",
            maxWidth: "100%",
          }}
        >
          <DataTable
            ariaLabel="students-list-table"
            columns={columns}
            data={students}
            // pagination
            // paginationPerPage={10}
            // paginationRowsPerPageOptions={[10, 20, 50]}
            progressPending={loading}
            progressComponent={
              <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
                Loading...
              </div>
            }
            noDataComponent={
              <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
                No records found for this grouping.
              </div>
            }
            defaultSortFieldId="avgScore"
            highlightOnHover
            striped
            customStyles={tableStyles}
            responsive
          />
        </div>

        <div
          className="modern-pagination"
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Pagination
            type="modern"
            itemsCount={totalItems}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            nextPage={handleNextPage}
            previousPage={handlePrevPage}
          />
        </div>
      </div>

      {/* Filter */}
      <FilterModal
        filterModal={filterModal}
        filterData={filterData}
        setFilterData={setFilterData}
        setFilterModal={setFilterModal}
        type={"student"}
        handleFilter={handleFilter}
      />

      {/* ── Permission modal ── */}
      <ModalWindow
        modal={permissionModal}
        toggleModal={() => setPermissionModal((prev) => !prev)}
        modalHeader="Assign permission to user"
        size="lg"
        id="tt-permissionModal"
        modalBody={
          <GranularPermissionModal
            widgetName="Learning Achievement"
            moduleName="Learning Achievement"
            header="List Students"
            activityName="student_achievement"
          />
        }
      />
    </>
  );
};

export default ListAllStudents;
